export const SUPPORTED_SOURCE_FILE_TYPES = ['xlsx', 'xls', 'csv']

const TAB_KEY = 'source'

const issue = (step, code, message, path = '/') => ({
    step,
    code,
    message,
    path,
    severity: 'error',
})

const COLUMN_BY_HEADER_EXTRACTOR = 'system.import.extractor.column_by_header'
const FIRST_PRESENT_COLUMN_EXTRACTOR = 'system.import.extractor.first_present_column'

const findComponent = (components, key, version = null) => components.find(component => (
    component.key === key
    && (version === null || component.version === version)
    && component.status !== 'deprecated'
)) || null

const componentReference = (component, argumentsValue = {}) => ({
    key: component.key,
    version: component.version,
    arguments: argumentsValue,
})

const selectedSourceTab = sourceState => {
    if (!sourceState.inspection || !Array.isArray(sourceState.inspection.tabs)) {
        return null
    }

    return sourceState.inspection.tabs.find(
        tab => tab.index === sourceState.selectedTabIndex
    ) || null
}

const buildSourceSelector = (sourceState, tab, components, issues) => {
    if (sourceState && sourceState.selector) {
        return JSON.parse(JSON.stringify(sourceState.selector))
    }

    const byName = findComponent(components, 'system.import.tab.by_name')
    if (byName && tab.name) {
        return componentReference(byName, {name: tab.name})
    }

    const firstTab = findComponent(components, 'system.import.tab.first')
    if (firstTab && tab.index === 0) {
        return componentReference(firstTab)
    }

    issues.push(issue(
        'source',
        'source_tab_selector_unavailable',
        'The catalog cannot select the inspected source tab.',
        `/source/tabs/${TAB_KEY}/selector`
    ))
    return null
}

const buildFieldMappings = ({
    mappingState,
    sourceTab,
    components,
    issues,
}) => {
    return mappingState.rows.reduce((fields, row) => {
        if (!row.targetFieldKey) {
            return fields
        }

        const path = `/mapping/tabs/${TAB_KEY}/fields/${row.targetFieldKey}`
        const extractorKey = row.extractorKey || COLUMN_BY_HEADER_EXTRACTOR
        const extractorVersion = Number.isInteger(row.extractorVersion)
            ? row.extractorVersion
            : null
        const extractor = findComponent(components, extractorKey, extractorVersion)
        const configuredSourceColumnIndexes = Array.isArray(row.sourceColumnIndexes)
            && row.sourceColumnIndexes.length
            ? row.sourceColumnIndexes
            : [row.sourceColumnIndex]
        const sourceColumnIndexes = extractorKey === FIRST_PRESENT_COLUMN_EXTRACTOR
            ? configuredSourceColumnIndexes
            : configuredSourceColumnIndexes.slice(0, 1)
        const columns = sourceColumnIndexes.map(index => (
            sourceTab.columns.find(item => item.index === index) || null
        ))
        const headers = columns.map(column => (
            column && typeof column.candidateHeader === 'string'
                ? column.candidateHeader.trim()
                : ''
        ))

        if (!extractor) {
            issues.push(issue(
                'mapping',
                extractorKey === COLUMN_BY_HEADER_EXTRACTOR
                    ? 'column_extractor_unavailable'
                    : 'source_extractor_unavailable',
                `Extractor ${extractorKey}${extractorVersion ? `@${extractorVersion}` : ''} is not available in this catalog.`,
                `${path}/extractor`
            ))
            return fields
        }

        if (!headers.length || headers.some(header => !header)) {
            issues.push(issue(
                'mapping',
                'source_column_header_missing',
                `Mapped field ${row.targetFieldKey} does not have usable source headers.`,
                `${path}/extractor`
            ))
            return fields
        }

        let extractorArguments = null
        if (extractorKey === COLUMN_BY_HEADER_EXTRACTOR) {
            extractorArguments = {column: headers[0]}
        } else if (extractorKey === FIRST_PRESENT_COLUMN_EXTRACTOR) {
            if (new Set(headers).size !== headers.length) {
                issues.push(issue(
                    'mapping',
                    'source_columns_duplicate',
                    `Mapped field ${row.targetFieldKey} contains duplicate fallback columns.`,
                    `${path}/extractor/arguments/columns`
                ))
                return fields
            }
            extractorArguments = {columns: headers}
        } else {
            issues.push(issue(
                'mapping',
                'source_extractor_unsupported',
                `Extractor ${extractorKey} is not supported by the visual source editor.`,
                `${path}/extractor`
            ))
            return fields
        }

        const pipeline = Array.isArray(row.pipeline) ? row.pipeline : []
        pipeline.forEach((element, index) => {
            if (element && !element.key && element.when
                && (!Array.isArray(element.then) || !element.then.length)) {
                issues.push(issue(
                    'mapping',
                    'conditional_branch_empty',
                    `Conditional branch for ${row.targetFieldKey} must contain at least one then step.`,
                    `${path}/pipeline/${index}/then`
                ))
            }
        })

        fields[row.targetFieldKey] = {
            extractor: componentReference(extractor, extractorArguments),
            pipeline,
        }
        return fields
    }, {})
}

export const buildManifestDefinition = ({
    sourceState,
    mappingState,
    productState,
    planningState,
    components = [],
} = {}) => {
    const issues = []
    const sourceTab = selectedSourceTab(sourceState || {})
    if (!sourceTab) {
        issues.push(issue('source', 'source_tab_missing', 'Inspect and select a source tab.'))
    }

    const selector = sourceTab
        ? buildSourceSelector(sourceState, sourceTab, components, issues)
        : null
    const fields = sourceTab
        ? buildFieldMappings({
            mappingState: mappingState || {rows: []},
            sourceTab,
            components,
            issues,
        })
        : {}

    if (!Object.keys(fields).length) {
        issues.push(issue(
            'mapping',
            'mapped_fields_missing',
            'Map at least one canonical field before creating a version.',
            `/mapping/tabs/${TAB_KEY}/fields`
        ))
    }
    if (!productState || !productState.driver || !productState.driverVersion
        || !productState.configuration) {
        issues.push(issue('product', 'target_missing', 'Complete the Import behavior step.', '/target'))
    }
    if (!planningState || !planningState.policy || !planningState.policyVersion) {
        issues.push(issue('planning', 'planning_missing', 'Select a planning policy.', '/planning'))
    }

    const definition = {
        manifestFormatVersion: 1,
        source: {
            ...(sourceState && sourceState.sourceExtras ? sourceState.sourceExtras : {}),
            type: sourceState && sourceState.sourceType
                ? sourceState.sourceType
                : 'spreadsheet',
            acceptedFileTypes: sourceState
                && Array.isArray(sourceState.acceptedFileTypes)
                && sourceState.acceptedFileTypes.length
                ? [...sourceState.acceptedFileTypes]
                : [...SUPPORTED_SOURCE_FILE_TYPES],
            tabs: {
                [TAB_KEY]: {
                    ...(sourceState && sourceState.tabExtras ? sourceState.tabExtras : {}),
                    ...(selector ? {selector} : {}),
                    headerRow: sourceState && sourceState.headerRow,
                    dataStartRow: sourceState && sourceState.dataStartRow,
                },
            },
        },
        mapping: {
            tabs: {
                [TAB_KEY]: {
                    ...(mappingState && mappingState.tabExtras ? mappingState.tabExtras : {}),
                    fields,
                    ...(
                        mappingState
                        && Array.isArray(mappingState.recordPipeline)
                        && mappingState.recordPipeline.length
                            ? {recordPipeline: mappingState.recordPipeline}
                            : {}
                    ),
                },
            },
        },
        target: {
            driver: productState && productState.driver,
            driverVersion: productState && productState.driverVersion,
            configuration: productState && productState.configuration,
        },
        planning: {
            policy: planningState && planningState.policy,
            policyVersion: planningState && planningState.policyVersion,
            configuration: planningState && planningState.configuration
                ? planningState.configuration
                : {},
        },
    }

    if (!Number.isInteger(definition.source.tabs[TAB_KEY].headerRow)
        || definition.source.tabs[TAB_KEY].headerRow < 1) {
        issues.push(issue(
            'source',
            'header_row_missing',
            'Choose a valid header row.',
            `/source/tabs/${TAB_KEY}/headerRow`
        ))
    }
    if (!Number.isInteger(definition.source.tabs[TAB_KEY].dataStartRow)
        || definition.source.tabs[TAB_KEY].dataStartRow < 1) {
        issues.push(issue(
            'source',
            'data_start_row_missing',
            'Choose a valid first data row.',
            `/source/tabs/${TAB_KEY}/dataStartRow`
        ))
    }

    return {definition, issues}
}

